import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import "./AIChat.css";

import { askLeonAI } from "../../Model/aiData";

// =====================================================
// CONSIGNMENT COMPONENTS
// =====================================================

import ConsignmentOptions from "./ConsignmentOptions";
import ConsignmentForm from "./ConsignmentForm";

// =====================================================
// LANGUAGES
// =====================================================

const languages = [
  {
    value: "auto",
    label: "Auto Detect",
  },
  {
    value: "en",
    label: "English",
  },
  {
    value: "fil",
    label: "Filipino",
  },
  {
    value: "es",
    label: "Spanish",
  },
  {
    value: "fr",
    label: "French",
  },
  {
    value: "zh",
    label: "Chinese",
  },
  {
    value: "ja",
    label: "Japanese",
  },
  {
    value: "ko",
    label: "Korean",
  },
];

// =====================================================
// AI CHAT
// =====================================================

const AIChat = () => {
  const [isOpen, setIsOpen] = useState(false);

  const [message, setMessage] = useState("");

  const [loading, setLoading] = useState(false);

  const [language, setLanguage] = useState("auto");

  // ===================================================
  // CONSIGNMENT MODE
  //
  // null
  // OPTIONS
  // FORM
  // ===================================================

  const [consignmentMode, setConsignmentMode] =
    useState(null);

  // ===================================================
  // MESSAGES
  // ===================================================

 const [messages, setMessages] = useState([
  {
    id: 1,
    sender: "ai",
    text:
      "Hello! I’m Leon AI, your AI assistant at Leon Gallery. 👋\n\nHow may I assist you today?\n\nYou can ask me about upcoming auctions, cocktails and previews, sold artworks, selling or consigning artwork, account registration, or anything about Leon Gallery.",
    quickActions: [
      {
        id: "upcoming-auctions",
        label: "🗓️ Upcoming Auctions",
        message: "What are the upcoming auctions?",
      },
       {
        id: "sold-artworks",
        label: "🔨 Sold Artworks",
        message: "Show me artworks that have been sold.",
      },
      {
        id: "cocktails-preview",
        label: "🍸 Cocktails & Preview",
        message: "Are there any upcoming cocktails or previews?",
      },
     
      {
        id: "sell-consign",
        label: "🖼️ Sell / Consign",
        message: "How can I sell or consign my artwork?",
      },
      {
        id: "my-account",
        label: "👤 My Account",
        message: "I need help with my Leon Gallery account.",
      },
      {
        id: "about-leon",
        label: "🏛️ About Leon Gallery",
        message: "Tell me about Leon Gallery.",
      },
    ],
  },
]);

  const messagesEndRef = useRef(null);

  const inputRef = useRef(null);

  // =====================================================
  // RENDER TEXT + CLICKABLE URLS
  // =====================================================

  const renderMessageWithLinks = (text) => {
    if (!text) {
      return null;
    }

    const urlRegex =
      /(https?:\/\/[^\s<]+)/g;

    const parts =
      String(text).split(urlRegex);

    const isUrl = (value) =>
      /^https?:\/\/[^\s<]+$/i.test(
        value
      );

    return parts.map(
      (part, index) => {
        if (isUrl(part)) {
          /*
           * Remove punctuation that Gemini may
           * accidentally place immediately after
           * the URL.
           *
           * Example:
           * https://leon-gallery.com/contact.
           *
           * becomes:
           * https://leon-gallery.com/contact
           */

          let cleanUrl = part;

          let trailingPunctuation = "";

          const match =
            cleanUrl.match(
              /([.,!?;:)\]}]+)$/
            );

          if (match) {
            trailingPunctuation =
              match[1];

            cleanUrl =
              cleanUrl.slice(
                0,
                -trailingPunctuation.length
              );
          }

          return (
            <React.Fragment
              key={index}
            >
              <a
                href={cleanUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ai-message-link"
              >
                {cleanUrl}
              </a>

              {trailingPunctuation}
            </React.Fragment>
          );
        }

        return (
          <React.Fragment
            key={index}
          >
            {part}
          </React.Fragment>
        );
      }
    );
  };

  // =====================================================
  // AUTO SCROLL
  // =====================================================

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [messages, loading, consignmentMode]);

  // =====================================================
  // FOCUS INPUT WHEN CHAT OPENS
  // =====================================================

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
    }
  }, [isOpen]);

  // =====================================================
  // SEND MESSAGE
  // =====================================================

 const sendMessage = async (customMessage = null) => {
  const trimmedMessage =
    (customMessage !== null
      ? customMessage
      : message
    ).trim();

    if (
      !trimmedMessage ||
      loading
    ) {
      return;
    }

    const userMessage = {
      id: Date.now(),
      sender: "user",
      text: trimmedMessage,
    };

    // -----------------------------------------------
    // ADD USER MESSAGE
    // -----------------------------------------------

    setMessages((prev) => [
      ...prev,
      userMessage,
    ]);

    // -----------------------------------------------
    // CLEAR INPUT
    // -----------------------------------------------

    setMessage("");

    // -----------------------------------------------
    // SHOW TYPING INDICATOR
    // -----------------------------------------------

    setLoading(true);

    try {
      const data =
        await askLeonAI({
          message:
            trimmedMessage,
          language,
        });

      // ---------------------------------------------
      // DEBUG
      // ---------------------------------------------

      console.log(
        "Leon AI response:",
        data
      );

      // ---------------------------------------------
      // ERROR RESPONSE
      // ---------------------------------------------

      if (!data?.success) {
        throw new Error(
          data?.message ||
            "Unable to get AI response."
        );
      }

      // ---------------------------------------------
      // AI RESPONSE
      // ---------------------------------------------

      const aiMessage = {
        id:
          Date.now() + 1,

        sender: "ai",

        text:
          data?.message ||
          "I'm sorry, I couldn't generate a response.",
      };

      setMessages((prev) => [
        ...prev,
        aiMessage,
      ]);

      // ---------------------------------------------
      // CONSIGNMENT ACTION
      // ---------------------------------------------
      //
      // Backend can return:
      //
      // action:
      // "CONSIGNMENT_OPTIONS"
      //
      // or:
      //
      // action:
      // "CONSIGNMENT_FORM"
      //
      // ---------------------------------------------

      if (
        data?.action ===
        "CONSIGNMENT_OPTIONS"
      ) {
        setConsignmentMode(
          "OPTIONS"
        );
      }

      if (
        data?.action ===
        "CONSIGNMENT_FORM"
      ) {
        setConsignmentMode(
          "FORM"
        );
      }
    } catch (error) {
      // ---------------------------------------------
      // LOG ERROR
      // ---------------------------------------------

      console.error(
        "AI Chat Error:",
        error
      );

      // ---------------------------------------------
      // FRIENDLY ERROR MESSAGE
      // ---------------------------------------------

      setMessages((prev) => [
        ...prev,
        {
          id:
            Date.now() + 1,

          sender: "ai",

          text:
            "I'm sorry, I'm temporarily unable to respond. Please try again in a moment.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // ENTER = SEND
  // SHIFT + ENTER = NEW LINE
  // =====================================================

  const handleKeyDown = (event) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey
    ) {
      event.preventDefault();

      sendMessage();
    }
  };

  // =====================================================
  // CLOSE CHAT
  // =====================================================

  const closeChat = () => {
    setIsOpen(false);
  };

  // =====================================================
  // CONSIGNMENT OPTIONS
  // =====================================================

  const renderConsignmentOptions = () => {
    if (
      consignmentMode !==
      "OPTIONS"
    ) {
      return null;
    }

    return (
      <div className="ai-consignment-wrapper">
        <ConsignmentOptions
          onSubmitHere={() => {
            setConsignmentMode(
              "FORM"
            );
          }}
        />
      </div>
    );
  };

  // =====================================================
  // CONSIGNMENT FORM
  // =====================================================

  const renderConsignmentForm = () => {
    if (
      consignmentMode !==
      "FORM"
    ) {
      return null;
    }

    return (
      <div className="ai-consignment-wrapper">
        <ConsignmentForm />
      </div>
    );
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <>
      {/* =================================================
          FLOATING CHAT BUTTON
      ================================================= */}

      {!isOpen && (
        <button
          type="button"
          className="ai-chat-button"
          onClick={() =>
            setIsOpen(true)
          }
          aria-label="Open Leon Gallery AI Assistant"
        >
          <span className="ai-button-logo-wrap">
            <img
              src="/images/leon-logo-ai.png"
              alt="Leon Gallery"
              className="ai-button-logo"
            />
          </span>

          <span className="ai-chat-label">
            Ask Leon AI
          </span>
        </button>
      )}

      {/* =================================================
          CHAT WINDOW
      ================================================= */}

      {isOpen && (
        <div className="ai-chat-window">

          {/* =============================================
              HEADER
          ============================================= */}

          <div className="ai-chat-header">

            <div className="ai-chat-header-info">

              <div className="ai-header-logo-wrap">
                <img
                  src="/images/leon-logo-ai.png"
                  alt="Leon Gallery"
                  className="ai-header-logo"
                />
              </div>

              <div className="ai-header-text">

                <div className="ai-chat-title">
                  LEON GALLERY
                </div>

                <div className="ai-chat-subtitle">
                  AI ASSISTANT
                </div>

                <div className="ai-chat-status">
                  <span className="ai-status-dot">
                  </span>

                  Online
                </div>

              </div>

            </div>

            <button
              type="button"
              className="ai-chat-close"
              onClick={closeChat}
              aria-label="Close chat"
            >
              ×
            </button>

          </div>

          {/* =============================================
              MESSAGES
          ============================================= */}

          <div className="ai-chat-messages">

          {messages.map((item) => (
  <div
    key={item.id}
    className={`ai-message-row ${
      item.sender === "user"
        ? "user"
        : "assistant"
    }`}
  >

    {/* =======================================
        AI LOGO
    ======================================= */}

    {item.sender === "ai" && (
      <div className="ai-message-avatar">
        <img
          src="/images/leon-logo-ai.png"
          alt="Leon Gallery"
        />
      </div>
    )}

    {/* =======================================
        MESSAGE
    ======================================= */}

    <div
      className={`ai-message ${
        item.sender === "user"
          ? "user-message"
          : "assistant-message"
      }`}
    >
      {renderMessageWithLinks(item.text)}

      {/* =======================================
          QUICK ACTIONS
      ======================================= */}

      {item.sender === "ai" &&
        item.quickActions?.length > 0 && (
          <div className="ai-quick-actions">
            {item.quickActions.map((action) => (
              <button
                key={action.id}
                type="button"
                className="ai-quick-action"
                onClick={() =>
                  sendMessage(
                    action.message
                  )
                }
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
    </div>

  </div>
))}

            {/* ===========================================
                CONSIGNMENT OPTIONS
            =========================================== */}

            {renderConsignmentOptions()}

            {/* ===========================================
                CONSIGNMENT FORM
            =========================================== */}

            {renderConsignmentForm()}

            {/* ===========================================
                TYPING INDICATOR
            =========================================== */}

            {loading && (
              <div className="ai-message-row assistant">

                <div className="ai-message-avatar">
                  <img
                    src="/images/leon-logo-ai.png"
                    alt="Leon Gallery"
                  />
                </div>

                <div className="ai-typing-message">

                  <div className="ai-thinking-label">
                    <span className="ai-thinking-icon">
                      ✦
                    </span>

                    <span className="ai-thinking-text">
                      Ask Leon
                    </span>
                  </div>

                  <div className="ai-thinking-status">
                    <span className="ai-shimmer-text">
                      Thinking...
                    </span>

                    <span className="ai-thinking-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </span>
                  </div>

                </div>

              </div>
            )}

            <div
              ref={messagesEndRef}
            />

          </div>

          {/* =============================================
              INPUT AREA
          ============================================= */}

          <div className="ai-chat-input-area">

            {/* ===========================================
                LANGUAGE
            =========================================== */}

            <div className="ai-language-selector">

              <span className="ai-language-icon">
                🌐
              </span>

              <select
                value={language}
                onChange={(event) =>
                  setLanguage(
                    event.target.value
                  )
                }
                disabled={loading}
                aria-label="Select language"
              >

                {languages.map(
                  (item) => (
                    <option
                      key={item.value}
                      value={item.value}
                    >
                      {item.label}
                    </option>
                  )
                )}

              </select>

            </div>

            {/* ===========================================
                INPUT ROW
            =========================================== */}

            <div className="ai-input-row">

              <textarea
                ref={inputRef}
                value={message}
                onChange={(event) =>
                  setMessage(
                    event.target.value
                  )
                }
                onKeyDown={
                  handleKeyDown
                }
                placeholder="Ask about auctions, artworks, bidding..."
                rows={1}
                disabled={loading}
              />

              <button
                type="button"
                className="ai-send-button"
                onClick={
                  sendMessage
                }
                disabled={
                  loading ||
                  !message.trim()
                }
                aria-label="Send message"
              >
                <span>
                  ➤
                </span>
              </button>

            </div>

          </div>

          {/* =============================================
              FOOTER
          ============================================= */}

          <div className="ai-chat-footer">

            <span>
              LEON GALLERY
            </span>

            <span>
              •
            </span>

            <span>
              AI ASSISTANT
            </span>

          </div>

        </div>
      )}

    </>
  );
};

export default AIChat;